import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { isUserAgreementNotAcceptedError } from '$core/models/user-agreement-not-accepted-error';
import { UserAgreementUpdatingService } from '$core/shell/user-agreement-updating-modal';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

/**
 * Marks a request already replayed after the agreement acceptance, so a repeated
 * rejection is not retried endlessly. Lives in the request context, so it is
 * never sent to the server.
 */
const RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Catches `403` responses with the `user_agreement_not_accepted` code and asks
 * for the agreement acceptance.
 *
 * The error is not rethrown: the user agreement updating modal is the only
 * feedback for this case, so neither the error screens nor the toasts and the
 * swal popups fired from the callers' error callbacks appear. Instead the
 * request is kept pending and replayed once the agreement has been accepted, so
 * the caller finally gets its data. Only a replayed request rejected again is
 * rethrown, since that points to a backend failure.
 */
@Injectable()
export class UserAgreementInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);

  /**
   * Resolved lazily: the service depends on `HttpClient` down the chain, so
   * injecting it eagerly would create a cyclic dependency while the interceptor
   * chain is being built and break the very first requests.
   */
  private get userAgreementUpdatingService(): UserAgreementUpdatingService {
    return this.injector.get(UserAgreementUpdatingService);
  }

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!isUserAgreementNotAcceptedError(error)) {
          return throwError(() => error);
        }

        // The agreement has already been accepted, yet the request is still
        // rejected: rethrow instead of retrying endlessly, so the caller gets
        // the actual error rather than a request completed without a value.
        if (request.context.get(RETRIED)) {
          return throwError(() => error);
        }

        const service = this.userAgreementUpdatingService;
        const accepted$ = service.accepted$.pipe(take(1));

        // Kept pending even when the modal is suppressed on the current route:
        // completing without a value resolves the callers' `toPromise()` with
        // `undefined`, while the agreement may still be accepted later in this
        // tab once the user navigates away.
        service.request();

        return accepted$.pipe(
          switchMap(() =>
            next.handle(
              request.clone({ context: request.context.set(RETRIED, true) })
            )
          )
        );
      })
    );
  }
}
